import moment from 'moment';
import { CHARGE_KIND, UNSELECTED_LABEL, ENABLE_GOTO_AGREEMENT_FEATURE } from '../constants';
import { getRomanAlphabetString } from '../utils/romanAlphabet';
import { getIsAna2 } from '../utils/Ana2';
import { unstable_batchedUpdates } from 'react-dom';
// import { PLAN_OPTION_CHARGE_KIND } from '../constants';

export const makePeopleInput = (people) => {
  const peopleInput = {
    adult: Number(people.adult),
  };

  if (people.childrenA) {
    peopleInput.childrenA = Number(people.childrenA);
  }
  if (people.childrenB) {
    peopleInput.childrenB = Number(people.childrenB); 
  }
  if(people.childrenC){
    peopleInput.childrenC = Number(people.childrenC);
  }
  if(people.children2){
    peopleInput.children2 = Number(people.children2);
  }
  if (people.infant) {
    peopleInput.infant = Number(people.infant);
  }

  return peopleInput;
};

export const makeNumberOfPeople = (people, airline, departureDate) => {
  const isAna2 = getIsAna2(airline, departureDate);
  if (!people) {
    return 0;
  }

  const adtNumberOfUnits = Number(people.adult);
  let chdNumberOfUnits = 0;

  if (people.childrenA) {
    chdNumberOfUnits += Number(people.childrenA);
  }
  if (people.childrenB) {
    chdNumberOfUnits += Number(people.childrenB);
  }
  if (people.childrenC) {
    chdNumberOfUnits += Number(people.childrenC);
  }
  if (people.children2 && isAna2) {
    chdNumberOfUnits += Number(people.children2);
  }

  return { adtNumberOfUnits, chdNumberOfUnits };
};

export const isEqualFlightsInput = (pre, current) => {
  if (!(pre && current)) {
    return false;
  }

  if (pre.adtNumberOfUnits !== current.adtNumberOfUnits) {
    return false;
  }

  if (pre.chdNumberOfUnits !== current.chdNumberOfUnits) {
    return false;
  }

  if (pre.airline !== current.airline) {
    return false;
  }

  if (pre.date !== current.date) {
    return false;
  }

  if (pre.departureAirportID !== current.departureAirportID) {
    return false;
  }

  if (pre.arrivalAirportID !== current.arrivalAirportID) {
    return false;
  }

  if (pre.includeConnectingFlight !== current.includeConnectingFlight) {
    return false;
  }

  return true;
};

export const makeFlightsInput = (people, airline, flight, departureDate, cacheFlag = false) => {
  const { adtNumberOfUnits, chdNumberOfUnits } = makeNumberOfPeople(people, airline, departureDate);

  return {
    adtNumberOfUnits,
    chdNumberOfUnits,
    airline,
    date: moment(flight.date).format('YYYY-MM-DD'),
    departureAirportID: flight.departureAirportId,
    arrivalAirportID: flight.arrivalAirportId,
    includeConnectingFlight: flight.includeConnectingFlight,
    departureDate: moment(departureDate).format('YYYY-MM-DD'),
    cacheFlag: cacheFlag === true ? "1" : "0",
  };
};

export const makeHotelsInput = (hotelsInput, flightsInput, departureDate, excludeLanding = 0) => {
  const {
    stayNights,
    pax,
    numberOfRooms,
    peoplePerRoom,
    directionId,
    prefectureId,
    areaId,
    districtId,
    roomType,
    mealCondition,
    tagIds,
    tagCodes,
    sortOrder,
    groupId,
    hotelCode,
    provider,
    tieupCompCD,
    coopOrganiJoin,
    priceFrom,
    priceTo,
    itemCD,
    keyword,
    siteId,
    searchCondition,
    showSD,
    showGroup,
    hideGroupName,
    excludeLandingFlag,
    hanbaiID,
    planPerHotel,
  } = hotelsInput;

  const {
    people,
    outbound,
    airline,
  } = flightsInput;

  const date = departureDate || outbound.date;
  const departureAirportId = outbound.departureAirportId;

  return {
    stayNights: Number(stayNights), // Int
    pax: Number(pax), // Int
    numberOfRooms: Number(numberOfRooms), // Int
    peoplePerRoom: Number(peoplePerRoom), // Int
    directionID: !hotelCode ? directionId : undefined, // ID
    prefectureID: !hotelCode ? prefectureId : undefined, // ID
    areaID: !hotelCode ? areaId : undefined, // ID
    districtID: !hotelCode ? districtId : undefined, // ID
    roomTypeKind: roomType, // ID
    mealConditonID: mealCondition, // ID
    tagIDs: tagIds, // [ID]
    tagCodes, // [String] tagIDに含まれていないCondition（こだわり条件）
    sortOrder, // 'Popularity', 'PriceAsc', 'PriceDesc'
    people: makePeopleInput(people), // TravelPeopleInput
    departure: { // DepartureInput
      date: moment(date).format('YYYY-MM-DD'),
    },
    groupID: groupId,
    hotelID: hotelCode,
    provider,
    tieupCompCD,
    COOP_ORGANI_JOIN: coopOrganiJoin,
    priceFrom,
    priceTo,
    ITEM_CD: itemCD,
    keyword,
    siteID: siteId,
    searchCondition: searchCondition,
    showSD,
    showGroup,
    hideGroupName,
    airline: airline,
    excludeLandingFlag: excludeLandingFlag,
    hanbaiID,
    planPerHotel,
    outDepAptCD: departureAirportId,
  };
};

export const makePlansInput = (hotelsInput, flightsInput, hotelId, excludeLanding = 0) => {
  const commonInput = makeHotelsInput(hotelsInput, flightsInput);

  // https://gitlab.fqsv.net/dp-plus/front/issues/156#note_29085
  if (commonInput.directionID) {
    delete commonInput.directionID;
  }

  if (hotelId) {
    delete commonInput.prefectureID;
    delete commonInput.areaId;
    delete commonInput.districtID;
  }

  if (commonInput.provider) {
    delete commonInput.provider;
  }
  
  // commonInput.excludeLandingFlag = excludeLanding ? 1 : 0;

  return {
    ...commonInput,
    hotelID: hotelId,
  };
};

export const makePlanInput = (planId, roomTypeId, hotelsInput, flightsInput, departureDate) => {
  const {
    stayNights,
    pax,
    numberOfRooms,
    peoplePerRoom,
    tieupCompCD,
    coopOrganiJoin,
    priceFrom,
    priceTo,
    itemCD,
    keyword,
    siteId,
    showSD,
    showGroup,
    hideGroupName,
  } = hotelsInput;

  const {
    people,
    outbound,
    airline,
  } = flightsInput;
  const date = departureDate || outbound.date;

  return {
    planID: planId,
    roomTypeID: roomTypeId,
    // hotelID: hotelId,
    stayNights: Number(stayNights), // Int
    pax: Number(pax), // Int
    numberOfRooms: Number(numberOfRooms), // Int
    peoplePerRoom: Number(peoplePerRoom), // Int
    people: makePeopleInput(people), // TravelPeopleInput
    departure: { // DepartureInput
      date: moment(date).format('YYYY-MM-DD'),
    },
    tieupCompCD,
    COOP_ORGANI_JOIN: coopOrganiJoin,
    priceFrom,
    priceTo,
    ITEM_CD: itemCD,
    keyword,
    siteID: siteId,
    showSD,
    showGroup,
    hideGroupName,
    airline : airline,
  };
};

export const getAirportNameFromDirectionsById = (directions, airportId) => {
  let arrivalAirportName = '';
  if (!directions.length) {
    return arrivalAirportName;
  }

  directions.forEach((d) => {
    const arrivalAirport = d.options.find((airport) => airport.id === airportId);
    if (arrivalAirport) {
      arrivalAirportName = arrivalAirport.name;
    }
  });

  return arrivalAirportName;
};

export const makeDatetime = (date, time) => {
  return `${date} ${time}:00`;
};

const makeSessionFlightWithoutConnecting = (flight, date) => {
  if (!flight) {
    return undefined;
  }

  const {
    id, airline, carrierName, departureTime, departureAirportName,
    arrivalTime, arrivalAirportName, seatStatus, price, childPrice, bookingClass,
    reservationCode, childReservationCode, departureAirportCode, arrivalAirportCode,
    operationCarrier, fareDisplayClass,
  } = flight;
  return {
    flightID: id,
    airline,
    carrierName,
    departureTime: makeDatetime(date, departureTime),
    departureAirportName,
    departureAirportCode,
    arrivalTime: makeDatetime(date, arrivalTime),
    arrivalAirportName,
    arrivalAirportCode,
    seatStatus,
    price,
    childPrice,
    bookingClass,
    fareDisplayClass,
    reservationCode,
    childReservationCode,
    operationCarrier,
  };
};

const makeSessionFlight = (flight, date, people) => {
  const { connectingFlights } = flight;
  const hasConnectingFlights = !!(connectingFlights && connectingFlights.length);
  return {
    ...makeSessionFlightWithoutConnecting(flight, date),
    connectingFlights: hasConnectingFlights ? {
      create: connectingFlights.map((c) => (
        makeSessionFlightWithoutConnecting(c, date)
      )),
    } : undefined,
    people: makePeopleInput(people),
  };
};

const makeSessionFlightSchedule = (outboundFlight, inboundFlight, outboundDate, inboundDate, flightsInput) => {
  const { people } = flightsInput;
  return {
    departure: { create: makeSessionFlight(outboundFlight, outboundDate, people) },
    arrival: { create: makeSessionFlight(inboundFlight, inboundDate, people) },
    airline: outboundFlight.airline,
  };
};

const makeSessionPlanQuestion = (questions) => {
  if (!questions) {
    return undefined;
  }

  return questions.map((q) => {
    const { title, selected, required } = q;
    return {
      title,
      selectionText: selected ? selected.text : undefined,
      selectionMessageText: selected ? selected.messageText : undefined,
      required,
    };
  });
};

const makeSessionPlans = (plans, hotelsInput) => {
  return plans.map((p) => {
    const { plan, startDate, stayNights, checkin } = p;
    const { id, name, stockStatus, mealCondition, hotel, totalAmount, roomType, questions, femaleOnlyPlan } = plan;
    const { numberOfRooms, peoplePerRoom } = hotelsInput;

    return {
      planID: id,
      name,
      stockStatus,
      mealCondition,
      checkin,
      hotelName: hotel.name,
      totalAmount,
      date: startDate,
      stayNights,
      numberOfRooms,
      peoplePerRoom,
      roomTypeID: roomType.id,
      questions: {
        create: makeSessionPlanQuestion(questions),
      },
      femaleOnlyPlan,
    };
  });
};

export const makeSessionPlanOptionsOnEntry = (planOptions, sortedPlanOptionKind) => {
  const newSessionPlanOption = planOptions.map((option, index) => {
    // __typenameフィールドを除去
    const { __typename, ...optionWithoutTypename } = option;
    const cleanChargeKinds = sortedPlanOptionKind[index].map(({ __typename, ...chargeKind }) => chargeKind);
    const cleanQuestions = option.questions ? option.questions.map(({ __typename, ...question }) => question) : [];
    
    return {
      ...optionWithoutTypename,
      chargeKinds: {
        create: cleanChargeKinds
      },
      questions: {
        create: cleanQuestions
      }
    };
  });
  return { lands: newSessionPlanOption };
};

const makeSessionPlanOptions = (planOptions) => {
    
    const landsInput = planOptions.map((option) => {
      const { goods, peopleCounts } = option;
      const questionInputs = goods.facilityQuestions ? goods.facilityQuestions.map((question) => {
        return {
          question: question.content ? question.content : null,
          isUserAnswerNeeded: question.required ? (question.required ? '1' : '0') : null,
          answer: question.answer ? question.answer : null,
        };
      }) : [];

      return {
        categoryDetail: goods.goodsDetailInfo.categoryDetail,
        useUnit: goods.goodsDetailInfo.useUnit,
        displayUnit: goods.goodsDetailInfo.displayUnit,
        goodsName: goods.goodsName,
        goodsPlanName: goods.goodsPlanName,
        landBookingNumber: goods.landBookingNumber ? goods.landBookingNumber : null,
        landCode: goods.goodsCode,
        landFacilityCode: goods.landFacilityCode,
        landFacilityName: goods.landFacilityName,
        noticeAndOthers: goods.noticeAndGuidance.noticeAndOthers,
        planCode: goods.goodsPlanCode,
        planExplanationDetail: goods.goodsBasicInfo.planExplanationDetail,
        useCount: peopleCounts ? getUseCount(peopleCounts, goods) : null,
        useDate: peopleCounts.DATE,
        AD: Number(peopleCounts.AD),
        SN: Number(peopleCounts.SN),
        OT: Number(peopleCounts.OT),
        ZP: Number(peopleCounts.ZP),
        MD: Number(peopleCounts.MD),
        CA: Number(peopleCounts.CA),
        CB: Number(peopleCounts.CB),
        CC: Number(peopleCounts.CC),
        IF: Number(peopleCounts.IF),
        ADPrice: getPrice(peopleCounts.chargesList, 'AD'),
        SNPrice: getPrice(peopleCounts.chargesList, 'SN'),
        OTPrice: getPrice(peopleCounts.chargesList, 'OT'),
        ZPPrice: getPrice(peopleCounts.chargesList, 'ZP'),
        MDPrice: getPrice(peopleCounts.chargesList, 'MD'),
        CAPrice: getPrice(peopleCounts.chargesList, 'CA'),
        CBPrice: getPrice(peopleCounts.chargesList, 'CB'),
        CCPrice: getPrice(peopleCounts.chargesList, 'CC'),
        IFPrice: getPrice(peopleCounts.chargesList, 'IF'),
        questions: {
          create: questionInputs,
        },
        chargeKinds: {
          create: goods.chargeKinds,
        },
      };
    });

    // 差額を取得
    function getPrice(chargesList, kind){
      const charge = chargesList.find((charge) => charge.kind === kind);
      return charge ? charge.price : null;
    }

    // 人数 or 台数の合計を計算
    function getUseCount(peopleCounts, goods){

      if(goods.goodsDetailInfo.useUnit === 'group'){
        const targetYear = peopleCounts.DATE.split('-')[0];
        const targetMonth = peopleCounts.DATE.split('-')[1];
        const targetDay = peopleCounts.DATE.split('-')[2];
        const targetCalendar = goods.calendarList.find((calendar) => calendar.year === targetYear && calendar.month === targetMonth && calendar.day === targetDay);
        const numberOfGroups = targetCalendar.chargesList.find((charge) => charge.kind === 'AD').numberOfGroups;
        return String(numberOfGroups);

      }else{
        const totalCount = Object.entries(peopleCounts)
          .filter(([key]) => (key !== "DATE" && key !== "chargesList"))
          .reduce((sum, [, value]) => sum + Number(value), 0);
        return String(totalCount);
      }
    }

    return landsInput;
};

export const makeCreateSessionInput = (
  outboundFlight, inboundFlight, outboundDate, inboundDate, plans, flightsInput, hotelsInput, planOptions, customerCode,
) => {
  return {
    flightSchedule: { create: makeSessionFlightSchedule(outboundFlight, inboundFlight, outboundDate, inboundDate, flightsInput) },
    plans: { create: makeSessionPlans(plans, hotelsInput) },
    people: makePeopleInput(flightsInput.people),
    lands: { create: makeSessionPlanOptions(planOptions) },
    otherSiteId: {create: {customerCode: customerCode}},
  };
};

export const makeNameInput = (nameType) => {
  const { first, last } = nameType;

  if ((!first || !last)) {
    return undefined;
  }

  return { first, last };
};

export const makeAlphabetNameInput = (alphabetName, phoneticName) => {
  
  //nullの場合は氏名をヘボン式ローマ字に変換して返す
  if(alphabetName === null){
    const { first, last } = phoneticName || {};
    if ((!first || !last)) {
      return undefined;
    }
    const firstAlphabetName = getRomanAlphabetString(first);
    const lastAlphabetName = getRomanAlphabetString(last);
    return {first: firstAlphabetName, last: lastAlphabetName};

  }else{
    const { first, last } = alphabetName;
    if ((!first || !last)) {
      return undefined;
    }
    return { first, last };
  }
}

const isValidTel = (tel) => {
  if (!tel) {
    return false;
  }

  const { first, second, third } = tel;

  return !!(first && second && third);
};

export const makeContactInput = (contactType) => {
  const { type, tel } = contactType;
  if (!type && !tel) {
    return undefined;
  }

  return { type, tel: isValidTel(tel) ? { first: tel.first, second: tel.second, third: tel.third } : undefined };
};

export const makeEmergencyContact = (emergencyContactType) => {
  const { relationship, name } = emergencyContactType;
  const contact = makeContactInput(emergencyContactType);

  if (!contact || !contact.tel || !relationship || !name) {
    return undefined;
  }

  return {
    tel: contact ? contact.tel : undefined, relationship, name };
};

export const makeSessionMember = (member, note) => {
  const {
    userID, name, phoneticName, alphabetName, birthday, sex, contact, email, emergencyContact: emergencyContactType,
    postalCode, prefectureID, address1, address2, totalPrice, entryDate,
  } = member;

  const emergencyContact = makeEmergencyContact(emergencyContactType);
  const prefectureIDZerofill = prefectureID.toString().padStart(2, '0');

  return {
    member: {
      userID,
      name: makeNameInput(name),
      phoneticName: makeNameInput(phoneticName),
      alphabetName: makeAlphabetNameInput(alphabetName, phoneticName),
      birthday,
      sex,
      contact: makeContactInput(contact),
      email,
      emergencyContact,
      note,
      postalCode: postalCode || undefined,
      prefectureID: prefectureIDZerofill || undefined,
      address1: address1 || undefined,
      address2: address2 || undefined,
      totalPrice: totalPrice || null,
      entryDate: entryDate || null,
    },
    wester: {
      deleteFlag: true,
    }
  };
};

const createBirthday = (year, month, date) => {
  const birthday = moment().year(Number(year)).month(Number(month) - 1).date(Number(date));
  return birthday.format('YYYY-MM-DD');
};

export const makeSessionAccompany = (customer, arrivalDate) => {
  const { state } = customer;
  return {
    name: {
      first: state.firstName, last: state.lastName, full: `${state.lastName || ''}${state.firstName || ''}`,
    },
    alphabetName: {
      first: state.firstAlphabetName,last: state.lastAlphabetName, full: `${state.lastAlphabetName || ''}${state.firstAlphabetName || ''}`,
    },
    birthday: createBirthday(state.birthYear, state.birthMonth, state.birthDate),
    sex: state.gender,
    roomAllocation: state.room,
    chargeKind: state.type,
    arrivalDate,
    infantIndicator: state.infantIndicator,
    totalPrice: state.totalPrice,
  };
};

export const makeSessionMemberAndAccompany = (entryState, email, arrivalDate, otherSiteMemberId) => {
  const { message } = entryState;
  const customers = entryState.customers.filter((c) => !c.state.representative);
  const representative = entryState.customers.find((c) => c.state.representative);
  const { state } = representative;

  // https://gitlab.fqsv.net/dp-plus/front/-/issues/314
  const relationshipEmergency = entryState.relationshipEmergency !== UNSELECTED_LABEL ? entryState.relationshipEmergency : null;

  return {
    accompanies: customers.map((c) => makeSessionAccompany(c, arrivalDate)),
    member: {
      name: {
        first: state.firstName, last: state.lastName, full: `${state.lastName || ''}${state.firstName || ''}`,
      },
      phoneticName: {
        first: state.firstPhoneticName,
        last: state.lastPhoneticName,
        full: `${state.lastPhoneticName || ''}${state.firstPhoneticName || ''}`,
      },
      alphabetName: {
        first: state.firstAlphabetName,
        last: state.lastAlphabetName,
        full: `${state.lastAlphabetName || ''}${state.firstAlphabetName || ''}`,
      },
      birthday: createBirthday(state.birthYear, state.birthMonth, state.birthDate),
      sex: state.gender,
      contact: {
        tel: {
          first: state.phoneNumber1,
          second: state.phoneNumber2,
          third: state.phoneNumber3,
          full: `${state.phoneNumber1 || ''}${state.phoneNumber2 || ''}${state.phoneNumber3 || ''}`
        },
        type: state.phoneType,
      },
      prefectureID: state.prefectureId,
      postalCode: state.postalCode1 + state.postalCode2,
      address1: state.address1,
      address2: state.address2,
      email,
      emergencyContact: {
        name: entryState.nameEmergency,
        relationship: relationshipEmergency,
        tel: {
          first: entryState.phoneNumberEmergency1,
          second: entryState.phoneNumberEmergency2,
          third: entryState.phoneNumberEmergency3,
          full: `${entryState.phoneNumberEmergency1 || ''}${entryState.phoneNumberEmergency2 || ''}${entryState.phoneNumberEmergency3 || ''}`,
        },
      },
      chargeKind: CHARGE_KIND.adult,
      arrivalDate,
      note: message,
      totalPrice: state.totalPrice,
      entryDate: state.entryDate,
    },
    agree: {
      transition_flag: ENABLE_GOTO_AGREEMENT_FEATURE ? '1' : '0',
    },
    otherSiteId: {
      otherSiteMemberId,
    }
  };
};

export const makeSessionCoupon = (coupon) => {
  if (!coupon) {
    return { coupon: null };
  }

  const { id, type, name, totalDiscount, goto } = coupon;
  return {
    coupon: {
      id,
      type,
      name,
      totalDiscount,
      goto: goto ? {
        regionalCoupon: goto.regionalCoupon, areaDiscount: goto.areaDiscount, communitySupport: !!goto.communitySupport,
      } : null,
    },
  };
};

export const makeSessionAgreeGuest = () => {
  return {
    agree: {
      gttaflag: '0',
      nta_gtta: '0',
      transition_flag: ENABLE_GOTO_AGREEMENT_FEATURE ? '1' : '0',
    },
  };
};

export const makeSessionAgreeMinorOnlyFlag = (minorOnlyFlag) => {
  return {
    agree: {
      minorOnlyFlag: minorOnlyFlag ? '1' : null,
    },
  };
};

export const makeSessionAgreeConsiderationFlag = (considerationFlag) => {
  return {
    agree: {
      considerationFlag: considerationFlag ? '1' : null,
    },
  };
};

export const makeGiftCardDetailInput = (giftCardDetails = []) => (
  giftCardDetails.map((g) => {
    const {
      giftCardKey, giftCardNumber, expirationDate, balance, inputAmount,
    } = g;

    return {
      giftCardKey, giftCardNumber, expirationDate, balance, inputAmount,
    };
  })
);

export const makeLandInput = (planId, roomTypeId, hotelsInput, flightsInput, outboundFlight) => {
  const {
    stayNights,
    numberOfRooms,
  } = hotelsInput;

  const {
    people,
    outbound,
    inbound,
  } = flightsInput;
  const { date } = outbound;
  const { date: returnDate } = inbound;

  return {
    planCD: planId,
    roomTypeCD: roomTypeId,
    stayNights: Number(stayNights),
    people: makePeopleInput(people),
    date: moment(date).format('YYYY-MM-DD'),
    numberOfRooms: numberOfRooms,
    returnDate: returnDate,
    airline: outboundFlight.airline,
  };
};

export const makeSessionWester = (wester) => {
  if (!wester || !wester.enabledWesterPoint) {
    return {
      wester: {
        usePoint: null,
      }
    };
  }

  const { usePoint } = wester;
  return {
    wester: {
      usePoint,
    }
  };
};